import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FicheDescriptiveOptions } from '../fiche-descriptive.component';
import { Feature, featureFromOl } from '@igo2/geo';
import { Constantes } from 'src/app/shared/constantes';
import { EntityStore, EntityTableColumn, EntityTableColumnRenderer, EntityTableComponent, EntityTableTemplate } from '@igo2/common/entity';
import { MessageService } from '@igo2/core/message';
import olFormatEsriJSON from 'ol/format/EsriJSON';

interface ArcGISQueryResponse {
  features: EsriFeature[];
  fields: EsriField[];
  numberReturned?: number;
  totalFeatures?: number;
  exceededTransferLimit?: boolean;
  error?: ArcGISError;
}

interface ArcGISError {
  code: number;
  message: string;
  details: string[];
  infos: any[];
}

interface EsriField {
  name: string;
  alias: string;
  type: string;
}

interface EsriFeature {
  attributes: { [key: string]: any };
  geometry?: Geometry;
}

interface Geometry {
  x?: number;
  y?: number;
  spatialReference?: SpatialReference;
}

interface SpatialReference {
  wkid?: number;
  latestWkid?: number;
}

@Component({
    selector: 'app-fiche-descriptive-table',
    templateUrl: './fiche-descriptive-table.component.html',
    styleUrls: ['./fiche-descriptive-table.component.scss'],
    imports: [
        CommonModule,
        EntityTableComponent
    ]
})
export class FicheDescriptiveTableComponent implements OnInit {
  @Input()
  ficheDescriptiveOptions: FicheDescriptiveOptions;
  @Output()
  dataLoaded = new EventEmitter<void>();
  store = new EntityStore<Feature>([]);
  tableTemplate: EntityTableTemplate;
  numberReturned: number;
  totalFeatures: number;
  messageApplication: { [key: string]: string } = {};
  get isIncompleteList(): boolean { return this.numberReturned !== this.totalFeatures; }
  get isThereSomethingToDisplay(): boolean { return this.store.count > 0; }
  get isNotSupported(): boolean { return !this.ficheDescriptiveOptions.url; }

  constructor(
    private httpClient: HttpClient,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {

    if (this.isNotSupported) return;

    switch (this.ficheDescriptiveOptions.requestType) {
      case 'wfs':
        this.loadWFS();
        break;
      case 'arcgisrest':
        this.loadArcGISRest();
        break;
      case 'wms':
        this.loadWMS();
        break;
      default:
        console.warn(`Invalid request type: ${this.ficheDescriptiveOptions.requestType}`);
    }
  }

  private loadWFS(): void {
    this.httpClient.post<{ features: Feature[], numberReturned?: number, totalFeatures?: number }>(
      this.ficheDescriptiveOptions.url,
      this.ficheDescriptiveOptions.params
    ).subscribe({
      next: response => {
        this.numberReturned = response.numberReturned ?? 0;
        this.totalFeatures = response.totalFeatures ?? 0;

        if (this.numberReturned < this.totalFeatures) {
          this.messageApplication.avertissement = Constantes.MessageApplicatif.Avertissement_29A.replace('{0}', this.numberReturned.toString());
        }
        else {
          this.messageApplication.avertissement = null;
        }

        if (response.features.length) {
          this.loadTable(response.features, response.features[0].properties);
        } else {
          this.ficheDescriptiveOptions.isLoaded = true;
          this.dataLoaded.emit();
        }
      },
      error: err => this.handleError(err)
    });
  }

  private loadArcGISRest(): void {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    let body = new HttpParams();

    let params = this.ficheDescriptiveOptions.params;
    for (const key in params) {
      const value = params[key];
      // Convert geometry to JSON strings
      const formattedValue = typeof value === 'object' ? JSON.stringify(value) : value;
      body = body.set(key, formattedValue);
    }

    this.httpClient.post<ArcGISQueryResponse>(
      this.ficheDescriptiveOptions.url,
      body.toString(),
      { headers }
    ).subscribe({
      next: response => {
        if (response.error) {
          console.error(`ArcGIS Rest error: ${response.error.code} - ${response.error.message}`);
          this.ficheDescriptiveOptions.isLoaded = true;
          this.dataLoaded.emit();
          return;
        }
        this.numberReturned = response.features.length;
        this.totalFeatures = response.exceededTransferLimit ? -1 : response.features.length;

        if (response.exceededTransferLimit) {
          this.messageApplication.avertissement = Constantes.MessageApplicatif.Avertissement_29A.replace('{0}', this.numberReturned.toString());
        }
        else {
          this.messageApplication.avertissement = null;
        }

        const parser = new olFormatEsriJSON();
        const olFeatures = parser.readFeatures(response);
        const features = olFeatures.map(ol => featureFromOl(ol, 'EPSG:3857'));

        if (features.length) {
          this.loadTable(features as any, response.features[0].attributes);
        } else {
          this.ficheDescriptiveOptions.isLoaded = true;
          this.dataLoaded.emit();
        }
      },
      error: err => this.handleError(err)
    });
  }

  //Code mort: tous les WMS sont isNotSupported
  private loadWMS(): void {
    this.httpClient.get<{ type: string, features: Feature[], numberReturned?: number }>(
      this.ficheDescriptiveOptions.url
    ).subscribe({
      next: response => {
        this.numberReturned = response.numberReturned ?? 0;
        if (this.numberReturned > 100) {
          this.messageApplication.avertissement = Constantes.MessageApplicatif.Avertissement_29A.replace('{0}', '100');
        }
        if (response.features.length) {
          this.loadTable(response.features, response.features[0].properties);
        } else {
          this.ficheDescriptiveOptions.isLoaded = true;
          this.dataLoaded.emit();
        }
      },
      error: err => this.handleError(err)
    });
  }

  private loadTable(features: Feature[], properties: object): void {
    this.tableTemplate = {
      selection: false,
      sort: true,
      columns: this.createColumns(properties)
    };
    this.store.insertMany(features);
    this.ficheDescriptiveOptions.isLoaded = true;
    this.dataLoaded.emit();
  }

  private createColumns(properties: object): EntityTableColumn[] {
    return Object.keys(properties).map(key => ({
      name: `properties.${key}`,
      title: key,
      renderer: EntityTableColumnRenderer.UnsanitizedHTML
    }));
  }

  private handleError(err: any): void {
    console.error('Error fetching data', err);
    this.ficheDescriptiveOptions.isLoaded = true;
    this.dataLoaded.emit();
  }
}
